import React, { useState, useEffect } from 'react';
import translate from "../../../config/translations/translate";
import {
  Modal,
  Table,
  UxTooltip
} from 'botbit-ui-components';
import DashboardPeriodSelector from '../dashboardPeriodSelector/DashboardPeriodSelector';
import { getQRScansLeaderboardByStore } from '../../../api/reviewLeaderboardApi';
import './QRScansLeaderboardModalStore.scss';
import { QRScanDownloadIcon, QRScanLeaderboardIcon, QRScanAddEmployeeIcon, QRScanCloseIcon} from '../DashboardV2ContainerIcons';
import { DashboardStoreSelector } from '../dashboardStoreSelector/DashboardStoreSelector';
import { useHistory } from 'react-router-dom';
import withEventTracking, { trackers } from '../../../config/withEventTracking';

const QRScansLeaderboardModalComponent = ({ 
  isOpen, 
  onClose, 
  storeId,
  storeName,
  t, 
  selectedPeriod, 
  stores,
  selectedStore,
  handleStoreChange,
  showStoreSelector,
  trackEvent
}) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentOrder, setCurrentOrder] = useState({ name: 'position', direction: 'asc' });
  const [localSelectedPeriod, setLocalSelectedPeriod] = useState({ value: 'LAST_7_DAYS' });
  const history = useHistory(); 

  const modalTimePeriodOptions = [
    { value: 'LAST_7_DAYS', label: t('timePeriod.last7Days') },
    { value: 'LAST_15_DAYS', label: t('timePeriod.last15Days') },
    { value: 'THIS_MONTH', label: t('timePeriod.thisMonth') },
    { value: 'PAST_MONTH', label: t('timePeriod.pastMonth') },
    { value: 'LAST_3_MONTHS', label: t('timePeriod.last3Months') },
    { value: 'LAST_6_MONTHS', label: t('timePeriod.last6Months') },
    { value: 'LAST_12_MONTHS', label: t('timePeriod.last12Months') },
  ];

  useEffect(() => {
    if (isOpen && selectedPeriod && selectedPeriod.value !== "LAST_30_DAYS" && selectedPeriod.value !== "THIS_YEAR") {
      setLocalSelectedPeriod(selectedPeriod);
    }
  }, [isOpen, selectedPeriod]);

  useEffect(() => {
    if (isOpen && storeId) {
      fetchLeaderboardData();
      
      // Track Review Leaderboard location view event when modal opens
      trackEvent('Reviews_Leaderboard_location_view', {
        storeId: storeId
      }, false, [trackers.MIXPANEL]);
    }
  }, [isOpen, storeId, localSelectedPeriod]);

  const handleLocalPeriodChange = (period) => {
    setLocalSelectedPeriod(period);
  };

  const fetchLeaderboardData = async () => {
    if (!storeId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      
      const response = await getQRScansLeaderboardByStore(storeId, { timePeriod: localSelectedPeriod });
      setLeaderboardData(response.data.items || []);
    } catch (error) {
      console.error("Error fetching QR Scans Leaderboard data:", error);
      setError(t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page, order, filters) => {
    const orderChanged = order && (currentOrder.name !== order.name || currentOrder.direction !== order.direction);
    
    if (orderChanged && order) {
      setCurrentOrder(order);
    }
        
    const sortName = order ? order.name : currentOrder.name;
    const sortDirection = order ? order.direction : currentOrder.direction;
    
    const sortedData = [...leaderboardData].sort((a, b) => {
      const valueA = a[sortName];
      const valueB = b[sortName];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      }
      
      return sortDirection === 'asc' 
        ? valueA - valueB 
        : valueB - valueA;
    });

    return {
      data: {
        data: {
          items: sortedData,
          totalSize: sortedData.length,
          pageSize: sortedData.length
        }
      }
    };
  };

  const handleTableChange = (type, { sortField, sortOrder }) => {
    if (type === 'sort') {
      setCurrentOrder({
        name: sortField,
        direction: sortOrder
      });
    }
  };

  const handleDownloadQR = async (qrLink, employeeName = '', employeeLastName = '', storeName = '') => {
    if (!qrLink) return;
    
    const fullUrl = qrLink.startsWith('http') ? qrLink : `https://${qrLink}`;
    
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(fullUrl)}`;
    
    // Helper function to normalize accented characters
    const normalizeText = (text) => {
      return text
        .normalize('NFD') // Decompose characters with their accents
        .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
        .replace(/\s+/g, '-') // Convert spaces to hyphens
        .replace(/[^A-Za-z0-9-]/g, ''); // Remove other special characters
    };
    
    // Generate a custom filename based on employee info and store name
    const cleanName = normalizeText(employeeName) || 'employee';
    const cleanLastName = normalizeText(employeeLastName) || '';
    const cleanStoreName = normalizeText(storeName) || 'store';
    
    const filename = cleanLastName 
      ? `${cleanName}-${cleanLastName}-${cleanStoreName}.png`
      : `${cleanName}-${cleanStoreName}.png`;
    
    try {
      // Fetch the QR code image
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename; // Use the custom filename
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      // Fallback to opening in new tab if download fails
      window.open(qrCodeUrl, '_blank');
    }
  };  

  const columns = [
    {
      dataField: 'position',
      title: '#',
      size: 15,
      sortable: true,
      formatter: (cellValue) => (
        <span className="leaderboard-position">#{cellValue}</span>
      ),
    },
    {
      dataField: 'adminName',
      title: (
        <div className="column-with-tooltip">
          {t('columns.name')}
          <UxTooltip
            message={t('columns.name.tooltip.content')}
            position="top"
          />
        </div>
      ),
      size: 35,
      sortable: true,
      formatter: (cellValue, row) => {
        const fullName = `${cellValue || ''} ${row.adminLastName || ''}`.trim();
        return <span className="employee-name">{fullName}</span>;
      },
    },
    {
      dataField: 'scanCount',
      title: (
        <div className="column-with-tooltip">
          {t('columns.qrScans')}
          <UxTooltip
            message={t('columns.qrScans.tooltip.content')}
            position="top"
          />
        </div>
      ),
      formatter: (cellValue) => (
        <span className="qr-scans-value">{cellValue}</span>
      ),
      size: 35,
      sortable: true,
      tooltip: {
        title: t('columns.qrScans'),
        content: t('tableTooltip.content')
      }
    },
    {
      dataField: 'qrLink',
      title: t('columns.actions'),
      size: 15,
      formatter: (cellValue, row) => (
        <span 
          className="download-qr-button"
          onClick={() => handleDownloadQR(cellValue, row.adminName, row.adminLastName, storeName)}
        >
          {t('buttons.downloadQR')}
          <QRScanDownloadIcon />
        </span>
      ),
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="qr-scans-leaderboard-modal"
      size="lg"
    >
      <div className="modal-close-button" onClick={onClose}>
        <QRScanCloseIcon />
      </div>
      
      <div className="qr-scans-leaderboard-header">
        <h1 className="store-name">{storeName || t('defaultStoreName')}</h1>
      </div>
      
      <div className="qr-scans-leaderboard-content">
        <div className="qr-scans-section-header">
          <div className="qr-icon-title">
            <div className="qr-icon">
              <QRScanLeaderboardIcon />   
            </div>
            <div className="title-description">
              <h2>{t('title')}</h2>
              <p>{t('description')} {t('rankingLink')}</p>
            </div>
          </div>
        </div>
        
        <div className="qr-scans-table-section">
          <div className="qr-scans-table-header">
            <h3>{t('tableTitle')}</h3>
            <div className="info-tooltip">
              <UxTooltip 
                title={t('tableTooltip.title')} 
                content={t('tableTooltip.content')} 
              />
            </div>
            {showStoreSelector && (
              <DashboardStoreSelector
                stores={stores}
                selectedStore={selectedStore}
                handleStoreChange={handleStoreChange}
                t={t}
              />
            )}
            <div className="period-selector-container">
              <DashboardPeriodSelector
                timePeriodOptions={modalTimePeriodOptions}
                selectedPeriod={localSelectedPeriod}
                handlePeriodChange={handleLocalPeriodChange}
                t={t}
                useDisabledDays={false}
              />
            </div>
          </div>
          
          <div className="qr-scans-table-container">
            {loading && (
              <div className="loading-message">{t('loading')}</div> 
            )}
            
            {error && (
              <div className="error-message">{error}</div>
            )}
            
            {!loading && !error && leaderboardData.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-users empty-state-icon"></i>
                <h3>{t("emptyState.title")}</h3>
                <h3>{t("emptyState.description")}</h3>
                <button 
                  className="btn-new-admin" 
                  onClick={() => history.push("/employee/new")}
                >
                  <i className="fas fa-user-plus"></i> {t("emptyState.newEmployee")}
                </button>
              </div>
            )}
            
            {!loading && !error && leaderboardData.length > 0 && (
              <Table
                getData={getTableData}
                columns={columns}
                showMobileColumnTitles={true}
                loading={loading}
                dataPath="data.data.items"
                elementKey="adminId"
                pagination={false}
                filterValues={{}}
                onTableChange={handleTableChange}
                remote={{ sort: true }}
              />
            )}
          </div>
        </div>
        
        <div className="qr-scans-footer">
          <button 
            className="abm-employees-button"
            onClick={() => history.push("/employee")}
          > 
            <QRScanAddEmployeeIcon />
            {t('buttons.abmEmployees')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

QRScansLeaderboardModalComponent.translations = {
  title: {
    es: "Tabla QR de posiciones",
    en: "QR Scan Leaderboard"
  },
  description: {
    es: "Mejora el potencial de tus empleados para pedir nuevos QR Scans.",
    en: "Improve your employees' potential to request new QR Scans."
  },
  rankingLink: {
    es: "Aquí puedes ver el ranking en base a los pedidos.",
    en: "Here you can see the ranking based on orders."
  },
  tableTitle: {
    es: "QR Scans por empleado",
    en: "QR Scans per employee"
  },
  locationSelector: {
    label: {
      es: "Tienda",
      en: "Store"
    }
  },
  timePeriod: {
    label: {
      es: "Período de tiempo",
      en: "Time period"
    },
    last7Days: {
      es: "Últimos 7 días",
      en: "Last 7 days"
    },
    last15Days: {
      es: "Últimos 15 días",
      en: "Last 15 days"
    },
    last30Days: {
      es: "Últimos 30 días",
      en: "Last 30 days"
    },
    thisMonth: {
      es: "Este mes",
      en: "This month"
    },
    pastMonth: {
      es: "Mes pasado",
      en: "Last month"
    },
    last3Months: {
      es: "Últimos 3 meses",
      en: "Last 3 months"
    },
    last6Months: {
      es: "Últimos 6 meses",
      en: "Last 6 months"
    },
    last12Months: {
      es: "Últimos 12 meses",
      en: "Last 12 months"
    }
  },
  defaultStoreName: {
    es: "Tienda",
    en: "Store"
  },
  loading: {
    es: "Cargando datos...",
    en: "Loading data..."
  },
  noData: {
    es: "No hay datos disponibles",
    en: "No data available"
  },
  emptyState: {
    title: {
      es: "No hay empleados creados aún",
      en: "No employees created yet"
    },
    description: {
      es: "Para empezar a usar esta funcionalidad, necesitas crear empleados y descargar sus códigos QR.",
      en: "To start using this feature, you need to create employees and download their QR codes."
    },
    newEmployee: {
      es: "Nuevo empleado",
      en: "New employee"
    }
  },
  tableTooltip: {
    title: {
      es: "QR Scans",
      en: "QR Scans"
    },
    content: {
      es: "Es la cantidad de scans únicos del QR del mozo. Esta métrica diferencia por cuenta de Google, asegurando confiabilidad en la unicidad",
      en: "It is the number of unique scans of the waiter's QR code. This metric differentiates by Google account, ensuring reliability in uniqueness"
    }
  },
  columns: {
    name: {
      es: "Nombre",
      en: "Name"
    },
    qrScans: {
      es: "QR scans",
      en: "QR scans",
    },
    actions: {
      es: "Acciones",
      en: "Actions"
    }
  },
  buttons: {
    downloadQR: {
      es: "Download QR",
      en: "Download QR"
    },
    abmEmployees: {
      es: "Empleados",
      en: "Employees"
    },
    close: {
      es: "Cerrar",
      en: "Close"
    }
  },
  errors: {
    fetchFailed: {
      es: "Error al cargar los datos. Por favor, intente nuevamente.",
      en: "Failed to load data. Please try again."
    }
  }
};

export const QRScansLeaderboardModalStore = withEventTracking(translate(QRScansLeaderboardModalComponent));